import { useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";
import { config } from "../config";
import "./styles/login.css";

// Replaces the legacy login.html. We swapped the hand-rolled Google Sign-In
// integration for @react-oauth/google, which manages script loading and
// rendering of the button for us. Apple, Facebook, and Microsoft use
// server-side OAuth redirects to /api/v1/auth/{provider}.
export function LoginPage() {
  const { isAuthenticated, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { t } = useLocale();

  useEffect(() => {
    // If we're already signed in just bounce back to /start (matches the
    // auth gate behaviour of the legacy main.js#switchPage).
    if (isAuthenticated) {
      const redirect =
        sessionStorage.getItem("postLoginRedirect") || "/start";
      sessionStorage.removeItem("postLoginRedirect");
      navigate(redirect.startsWith("/") ? redirect : `/${redirect}`, {
        replace: true,
      });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const oauthError = searchParams.get("error");
    if (!oauthError) return;
    if (oauthError.startsWith("apple")) {
      toast.show(t("auth.appleFailed"), "danger");
    } else if (oauthError.startsWith("microsoft")) {
      toast.show(t("auth.microsoftFailed"), "danger");
    } else if (oauthError.startsWith("facebook")) {
      toast.show(t("auth.facebookFailed"), "danger");
    } else {
      return;
    }
    searchParams.delete("error");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, t, toast]);

  const startOAuthLogin = (provider: "apple" | "facebook" | "microsoft") => {
    const next =
      sessionStorage.getItem("postLoginRedirect") || "/start";
    const url = `${config.apiUrl}/auth/${provider}?next=${encodeURIComponent(next)}`;
    window.location.href = url;
  };

  return (
    <section className="content">
      <h1>{t("auth.loginTitle")}</h1>

      <div
        className="social-login-container"
        style={{ maxWidth: 300, margin: "0 auto" }}
      >
        <div id="googlePromptContainer">
          <GoogleLogin
            onSuccess={async (resp) => {
              if (!resp.credential) {
                toast.show(t("auth.googleFailed"), "danger");
                return;
              }
              try {
                const data = await signInWithGoogle(resp.credential);
                if (data.tos === null) {
                  navigate("/register", { replace: true });
                  return;
                }
                const redirect =
                  sessionStorage.getItem("postLoginRedirect") || "/start";
                sessionStorage.removeItem("postLoginRedirect");
                navigate(
                  redirect.startsWith("/") ? redirect : `/${redirect}`,
                  { replace: true },
                );
              } catch (err) {
                console.error(err);
                toast.show(
                  t("auth.loginFailed", { error: (err as Error).message }),
                  "danger",
                );
              }
            }}
            onError={() => toast.show(t("auth.googleFailed"), "danger")}
            useOneTap={false}
            text="signin_with"
            shape="rectangular"
            theme="outline"
            size="large"
          />
        </div>

        <button
          type="button"
          className="social-login-btn apple-btn"
          onClick={() => startOAuthLogin("apple")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          {t("auth.apple")}
        </button>

        <button
          type="button"
          className="social-login-btn meta-btn"
          onClick={() => startOAuthLogin("facebook")}
        >
          <svg width="20" height="20" viewBox="0 0 100 62" aria-hidden="true">
            <path
              fill="#0866FF"
              d="M50.2 0c-22.3 0-36.8 20-36.8 31s6.5 31 22.5 31c9.8 0 17.5-9.6 22.9-18.7 5.4 9.1 13.1 18.7 22.9 18.7C97.7 62 104 42 104 31S88.6 0 66.3 0c-7.6 0-14 6.2-18.9 13.4C42.8 6.2 35.9 0 27.3 0 11.2 0 0 17.2 0 31s9.5 31 24.3 31c6.6 0 12.2-4.2 17.1-10.9"
            />
          </svg>
          {t("auth.facebook")}
        </button>

        <button
          type="button"
          className="social-login-btn microsoft-btn"
          onClick={() => startOAuthLogin("microsoft")}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#f25022" d="M1 1h10v10H1z" />
            <path fill="#00a4ef" d="M13 1h10v10H13z" />
            <path fill="#7fba00" d="M1 13h10v10H1z" />
            <path fill="#ffb900" d="M13 13h10v10H13z" />
          </svg>
          {t("auth.microsoft")}
        </button>
      </div>
    </section>
  );
}
